import dbCodes from '../server/dbCodes.js'
import type {Order} from '../server/functions/orders/orders.types.js'
import type {Reseller} from '../server/functions/resellers/resellers.types.js'

type OrderLike = Pick<Order, 'priceExplanation'> & {
    fees?: Order['fees'] | string
}

type ResellerLike = Pick<Reseller, 'settings'>

const formatFee = (name: string, quantity?: number) => {
    return (quantity || 1) > 1 ? `${quantity}x ${name}` : name
}

// Like the administration panel: a fee that costs € 0,00 is not a surcharge
const hasCost = (fee?: {subtotal?: string}) => fee?.subtotal !== '0,00'

const getOptionName = (option?: string, reseller?: ResellerLike | null) => {
    if (!option) {
        return ''
    }

    return dbCodes.pickupAndDeliveryOptions[option] ||
        reseller?.settings.orders.customOptions?.find((customOption) => customOption.id === option)?.name ||
        ''
}

/**
 * The fees of an order as shown in the Toeslagen column.
 *
 * The source of truth is the price explanation. Besides priceExplanation.fees this includes the
 * other fee-like parts shown in Administration (extra stop, pickup/delivery address fees, extra
 * collo, colli type fees, legacy pickup/delivery option fees and zone fees). The fuel surcharge is
 * deliberately excluded.
 *
 * Only orders without a price explanation (older orders, orders without a price table) fall back
 * to the deprecated order.fees text field.
 */
export function getOrderFeesText(order?: OrderLike | null, reseller?: ResellerLike | null) {
    const priceExplanation = order?.priceExplanation

    if (priceExplanation) {
        const feeParts = [
            ...((priceExplanation.pickupAndDeliveryOptions || []).map((fee) => {
                const name = getOptionName(fee?.option, reseller)

                return name ? formatFee(name, fee?.nrOfAddresses) : ''
            })),
            ...((priceExplanation.fees || []).map((fee) => {
                return fee?.name ? formatFee(fee.name, fee.quantity) : ''
            })),
            priceExplanation.feeExtraStop?.nrOfStops && hasCost(priceExplanation.feeExtraStop) ? formatFee('Extra stop', priceExplanation.feeExtraStop.nrOfStops) : '',
            priceExplanation.feeForPickupAddress?.nrOfAddresses && hasCost(priceExplanation.feeForPickupAddress) ? formatFee('Ophaaladres', priceExplanation.feeForPickupAddress.nrOfAddresses) : '',
            priceExplanation.feeForDeliveryAddress?.nrOfAddresses && hasCost(priceExplanation.feeForDeliveryAddress) ? formatFee('Bezorgadres', priceExplanation.feeForDeliveryAddress.nrOfAddresses) : '',
            priceExplanation.feeForExtraCollo?.nrOfColli && hasCost(priceExplanation.feeForExtraCollo) ? formatFee('Extra collo', priceExplanation.feeForExtraCollo.nrOfColli) : '',
            ...((priceExplanation.colliTypes || []).map((colliType) => {
                return colliType?.type ? formatFee(`Colli: ${colliType.type}`, colliType.nrOfColli) : ''
            })),
            priceExplanation.zoneFees?.zones?.length ? `Bezorggebied ${priceExplanation.zoneFees.zones[0]}${priceExplanation.zoneFees.zones[1] ? ` - ${priceExplanation.zoneFees.zones[1]}` : ''}` : ''
        ]

        return feeParts.filter((name) => name).join(', ')
    }

    // order.fees is already flattened to the display text on the filtered orders in src/utils/filter.js
    if (typeof order?.fees === 'string') {
        return order.fees
    }

    return order?.fees?.join(', ') || ''
}
